import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
  const { user, logout, isPro } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile nav on route change
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const navLinkStyle = (path) => ({
    color: isActive(path) ? '#0a0a0a' : '#6b6b6b',
    fontFamily: 'var(--font-mono)',
    fontSize: 12, fontWeight: 400, letterSpacing: '1.5px', textTransform: 'uppercase',
    padding: '0 14px', height: 56,
    display: 'flex', alignItems: 'center',
    borderBottom: isActive(path) ? '2px solid #0a0a0a' : '2px solid transparent',
    transition: 'color 0.15s', textDecoration: 'none'
  });

  return (
    <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'var(--paper)', borderBottom: '2px solid var(--ink)', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

      {/* Logo */}
      <Link to="/" style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)', fontSize: 22, fontWeight: 400, letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', flexShrink: 0 }}>
        <span style={{ width: 28, height: 28, background: 'var(--ink)', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--paper)', fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 700, letterSpacing: '-1px', flexShrink: 0 }}>BM</span>
        <span className="nav-logo-text">BioMedArticles</span>
      </Link>

      {/* Desktop center nav */}
      <div style={{ display: 'flex', gap: 0 }} className="desktop-nav">
        <Link to="/" style={navLinkStyle('/')}>Search</Link>
        <Link to="/how-it-works" style={navLinkStyle('/how-it-works')}>How It Works</Link>
        <Link to="/faq" style={navLinkStyle('/faq')}>FAQ</Link>
        <Link to="/knowledge" style={navLinkStyle('/knowledge')}>Knowledge</Link>
        <Link to="/blog" style={navLinkStyle('/blog')}>Blog</Link>
        {user && <Link to="/profile" style={navLinkStyle('/profile')}>Profile</Link>}
        {isAdmin && <Link to="/admin" style={navLinkStyle('/admin')}>Admin</Link>}
      </div>

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {user ? (
          <>
            {isPro() ? (
              <span style={{ background: 'transparent', color: 'var(--ink)', fontSize: 10, fontWeight: 500, padding: '3px 9px', borderRadius: 0, letterSpacing: '2px', textTransform: 'uppercase', border: '1px solid var(--ink)', fontFamily: 'var(--font-mono)' }}>PRO</span>
            ) : (
              <button
                style={{ background: 'var(--ink)', color: 'var(--paper)', border: '1.5px solid var(--ink)', padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }}
                onClick={() => navigate('/profile?upgrade=true')}
              >
                Upgrade
              </button>
            )}
            <div style={{ position: 'relative' }} ref={menuRef}>
              <button
                style={{ background: 'transparent', border: '1px solid var(--ink)', color: 'var(--ink)', width: 32, height: 32, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                onClick={() => setMenuOpen(o => !o)}
              >
                {initials}
              </button>
              {menuOpen && (
                <div style={{ position: 'absolute', top: 42, right: 0, background: 'var(--card)', border: '1px solid var(--ink)', borderRadius: 0, minWidth: 210, boxShadow: '6px 6px 0 rgba(10,10,10,0.08)', zIndex: 200 }}>
                  <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--line)' }}>
                    <div style={{ color: 'var(--ink)', fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-display)' }}>{user.name}</div>
                    <div style={{ color: 'var(--muted-2)', fontSize: 11, marginTop: 3, fontFamily: 'var(--font-mono)' }}>{user.email}</div>
                  </div>
                  {[
                    { label: 'Profile', path: '/profile' },
                    ...(isAdmin ? [{ label: 'Admin Panel', path: '/admin' }] : []),
                  ].map(item => (
                    <div key={item.path} style={{ padding: '11px 16px', color: '#3a3a3a', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid var(--line)', transition: 'background 0.1s', fontFamily: 'var(--font-mono)', letterSpacing: '0.5px' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--paper-2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={() => { navigate(item.path); setMenuOpen(false); }}
                    >{item.label}</div>
                  ))}
                  <div style={{ padding: '11px 16px', color: 'var(--danger)', fontSize: 13, cursor: 'pointer', transition: 'background 0.1s', fontFamily: 'var(--font-mono)', letterSpacing: '0.5px' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--paper-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    onClick={() => { logout(); setMenuOpen(false); navigate('/'); }}
                  >→ Sign Out</div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <button style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }} onClick={() => navigate('/login')}>Sign in</button>
            <button style={{ background: 'var(--ink)', color: 'var(--paper)', border: '1.5px solid var(--ink)', padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }} onClick={() => navigate('/register')}>Get Started</button>
          </>
        )}

        {/* Mobile hamburger */}
        <button
          className="mobile-menu-btn"
          style={{ display: 'none', background: 'none', border: 'none', color: 'var(--ink)', fontSize: 22, cursor: 'pointer', padding: '4px', marginLeft: 4 }}
          onClick={() => setMobileNavOpen(o => !o)}
        >
          {mobileNavOpen ? '×' : '☰'}
        </button>
      </div>

      {/* Mobile nav dropdown */}
      {mobileNavOpen && (
        <div style={{ position: 'absolute', top: 56, left: 0, right: 0, background: 'var(--paper)', borderBottom: '2px solid var(--ink)', zIndex: 200, padding: '8px 0' }}>
          {[
            { to: '/', label: 'Search' },
            { to: '/how-it-works', label: 'How It Works' },
            { to: '/faq', label: 'FAQ' },
            { to: '/knowledge', label: 'Knowledge' },
            { to: '/blog', label: 'Blog' },
            ...(user ? [{ to: '/profile', label: 'Profile' }] : []),
            ...(isAdmin ? [{ to: '/admin', label: 'Admin' }] : []),
          ].map(l => (
            <Link key={l.to} to={l.to} style={{ display: 'block', padding: '12px 24px', color: isActive(l.to) ? 'var(--ink)' : 'var(--muted)', fontSize: 13, borderBottom: '1px solid var(--line)', fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{l.label}</Link>
          ))}
          {!user && (
            <div style={{ padding: '12px 24px', display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '9px', fontSize: 12, borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }} onClick={() => navigate('/login')}>Sign in</button>
              <button style={{ flex: 1, background: 'var(--ink)', color: 'var(--paper)', border: '1.5px solid var(--ink)', padding: '9px', fontSize: 12, fontWeight: 700, borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }} onClick={() => navigate('/register')}>Get Started</button>
            </div>
          )}
          {user && (
            <div style={{ padding: '12px 24px' }}>
              <button style={{ width: '100%', background: 'transparent', border: '1.5px solid var(--ink)', color: 'var(--danger)', padding: '9px', fontSize: 12, borderRadius: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', letterSpacing: '1px', textTransform: 'uppercase' }} onClick={() => { logout(); navigate('/'); }}>Sign Out</button>
            </div>
          )}
        </div>
      )}

      {/* Mobile CSS via style tag */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .nav-logo-text { display: none; }
        }
        @media (max-width: 480px) {
          .desktop-nav { display: none !important; }
        }
      `}</style>
    </nav>
  );
}
